import type { FeeDiscrepancy } from "../cases/case-model";
import { TreesloGridSortSpec } from "src/app/shared/components/treeslo-grid/treeslo-grid.model";

/**
 * Enum with all claim types
 */
export enum ClaimType {
  Lost_Outbound = "Lost_Outbound",
  Lost_Inbound = "Lost_Inbound",
  Adjustments_Damaged = "Adjustments_Damaged",
  Refunds_Never_Returned_FBA = "Refunds_Never_Returned_FBA",
  ReAnalysis = "ReEvaluations",
  Wrong_FBA_Fees = "Wrong_FBA_Fees",
  Unknown = "Unknown",
}

export interface ClaimReimbursement {
  amount: number;
  fnsku: string;
  isInventoryReturn: boolean;
}

export interface ClaimShipmentItem {
  shipmentId: string;
  sellerSku: string;
  fnsku: string;
  quantityShipped: number;
  quantityReceived: number;
  quantityInCase: number;
  releaseDate: string;
  isExpanded?: boolean;
}

/**
 * Base interface with common fields across all claim types
 */
export interface ClaimDetailBase {
  claimId: string;
  claimType: ClaimType;
  createdAt: string;
  status: string;
  storeId: string;
  resolutionType: string;
  updatedAt?: string;
  claimTypeName?: string;
  storeName?: string;
  statusName?: string;
  canReset?: boolean;
  resetPreview?: ClaimResetPreview;
  /** Potential reimbursement from list/detail API; used to suggest expectedReimbAmount when filing. */
  amountPotential?: number;
}

export interface ClaimResetPreview {
  previousStatus: string;
  lastMutationResolutionType: string;
  lastMutationAt: string;
}

/**
 * Lost Outbound Claim Detail
 */
export interface ClaimDetailDTO_LostOutbound extends ClaimDetailBase {
  claimType: ClaimType.Lost_Outbound;
  quantity: number;
  eventDate: string;
  shipmentDate: string;
  fnsku: string;
  removalId: string;
  trackingNumber: string;
  carrier?: string;
  carrierName?: string;
  carrierUrl?: string;
  providerShipmentDetailsUrl?: string;
  description: string;
  sku: string;
}

/**
 * Lost Inbound Claim Detail
 */
export interface ClaimDetailDTO_LostInbound extends ClaimDetailBase {
  claimType: ClaimType.Lost_Inbound;
  eventDate: string;
  description?: string;
  shipmentId?: string;
  sku?: string;
  quantity?: number;
}

/**
 * Adjustments Damaged Claim Detail
 */
export interface ClaimDetailDTO_AdjustmentsDamaged extends ClaimDetailBase {
  claimType: ClaimType.Adjustments_Damaged;
  description: string;
  eventDate: string;
  fnsku: string;
  quantity: number;
  referenceId: string;
  sku: string;
  shipmentItems: ClaimShipmentItem[];
  reimbursements: ClaimReimbursement[];
}

/**
 * Refunds Never Returned FBA Claim Detail
 */
export interface ClaimDetailDTO_RefundsNeverReturnedFBA
  extends ClaimDetailBase {
  claimType: ClaimType.Refunds_Never_Returned_FBA;
  quantity: number;
  refundAmount: number;
  orderId: string;
  eventDate: string;
  refundDate: string;
  orderDate: string;
  description: string;
  sku: string;
}

/**
 * Wrong FBA Fees Claim Detail
 */
export interface ClaimDetailDTO_WrongFBAFees extends ClaimDetailBase {
  claimType: ClaimType.Wrong_FBA_Fees;
  quantity: number;
  currency: string;
  countryCode: string;
  eventDate: string;
  measurementUnits: string;
  fnsku: string;
  discrepancies: FeeDiscrepancy[];
  weightUnits: string;
  description: string;
  claimAmount: number;
  cogsId?: string;
}

/**
 * Reanalysis Claim Detail
 */
export interface ClaimDetailDTO_ReAnalysis extends ClaimDetailBase {
  claimType: ClaimType.ReAnalysis;
  quantity: number;
  haveDocument: boolean;
  reimbursementType: string;
  countryCode: string;
  claimReimbursementId: string;
  eventDate: string;
  expectedValue: number;
  providerPaidValue: number;
  fnsku: string;
  discrepancy: number;
  description: string;
  claimAmount: number;
  sku: string;
}

/**
 * Discriminated union type for all claim detail types
 */
export type ClaimDetailDTO =
  | ClaimDetailDTO_LostOutbound
  | ClaimDetailDTO_LostInbound
  | ClaimDetailDTO_AdjustmentsDamaged
  | ClaimDetailDTO_RefundsNeverReturnedFBA
  | ClaimDetailDTO_ReAnalysis
  | ClaimDetailDTO_WrongFBAFees;
export interface ClaimItemModel {
  claimType: string;
  referenceId: string;
  fnsku: string;
  sku: string;
  quantity: number;
  eventDate: string;
  amountPotential: number;
  status: string;
  storeId: string;
  claimId: string;
  resolutionType: string;
  description: string;
  createdAt: string;
  updatedAt: string;

  storeName: string;
  claimTypeName: string;
  statusName: string;
  provider: string;

  canReset?: boolean;
  resetPreview?: ClaimResetPreview;

  // Type-specific optional fields
  currency?: string;
  countryCode?: string;
  discrepancies?: FeeDiscrepancy[];
  measurementUnits?: string;
  weightUnits?: string;
  claimAmount?: number;
  removalId?: string;
  shipmentId?: string;
  orderId?: string;
  refundAmount?: number;
  orderDate?: string;
  refundDate?: string;
  claimReimbursementId?: string;
  expectedValue?: number;
  providerPaidValue?: number;
  discrepancy?: number;
  reimbursementType?: string;
  haveDocument?: boolean;
  shipmentItems?: ClaimShipmentItem[];
  inventoryReimbursed?: number;
  reimbursements?: ClaimReimbursement[];
  cashReimbursed?: number;
  returnId?: string;
  amount?: number;
  filedDate?: string;
  shipmentDate?: string;
  trackingNumber?: string;
  carrier?: string;
  carrierName?: string;
  carrierUrl?: string;
  providerShipmentDetailsUrl?: string;
  cogsId?: string;
}

export interface ClaimsGetDTO {
  cursor: string;
  limit: number;
  sort: TreesloGridSortSpec[] | string;
  items: ClaimItemModel[];
}
export interface FilterClaim {
  storeId?: string;
  startDate?: string;
  endDate?: string;
  cursor?: string;
  limit?: string;
  sort?: string;
}
